/**
 * SBX-505: the platform keeps ONE active session per account - a
 * second login rewrites `IoUser.active_token`, and the first
 * session's next boot-time `verifyToken` is refused ("Access token
 * is not activated", core/schema.py Verify.mutate). Until now that
 * kick was a silent bounce to /login ("random logouts" on shared
 * municipal accounts, prod-audit P2-9).
 *
 * This seam names the kick: the router marks it when the verify
 * refusal carries the kicked shape, and the login page consumes the
 * mark ONCE to explain what happened. sessionStorage on purpose -
 * the explanation belongs to this tab's bounce, not to next week's
 * login.
 *
 * Classifying the refusal: the backend LOCALIZES its error messages
 * per the account's language (observed live: the Danish demo user
 * gets "Adgangstilladelse er ikke aktiveret"), so text matching is
 * not the primary signal. The principled discriminator: verify
 * refused a token that is well-formed and UNEXPIRED by its own exp
 * claim - the only backend path that refuses such a token is the
 * active-token mismatch, i.e. the kick. Ordinary expiry fails the
 * client-side exp check and stays a silent ordinary logout. The
 * English message text remains a fast-path confirmation.
 */

const KEY = 'wh.signedInElsewhere'

/** The backend's kicked-session refusal in source English. */
const KICKED_MESSAGE = 'Access token is not activated'

interface ErrorEntry {
  message?: unknown
}

/** Both refusal shapes Apollo produces: a rejected ApolloError
 * (message + graphQLErrors) and a resolved result whose `errors`
 * ride beside `data.verifyToken: null` (the shape observed live). */
function errorEntries(errorish: unknown): ErrorEntry[] {
  if (!errorish || typeof errorish !== 'object') return []
  const doc = errorish as {
    message?: unknown
    graphQLErrors?: ErrorEntry[]
    errors?: ErrorEntry[]
  }
  const entries: ErrorEntry[] = []
  if (typeof doc.message === 'string' && doc.message) {
    entries.push({ message: doc.message })
  }
  if (Array.isArray(doc.graphQLErrors)) entries.push(...doc.graphQLErrors)
  if (Array.isArray(doc.errors)) entries.push(...doc.errors)
  return entries
}

/** True when the token's OWN exp claim says it has real life left
 * (30s skew guard); malformed tokens are never "alive". @pure */
export function tokenLooksAlive(
  token: string | null | undefined,
  nowMs: number = Date.now()
): boolean {
  if (!token) return false
  try {
    const payload = token.split('.')[1]
    if (!payload) return false
    const decoded = JSON.parse(
      window.atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    ) as { exp?: unknown }
    return (
      typeof decoded.exp === 'number' && decoded.exp * 1000 > nowMs + 30_000
    )
  } catch (e) {
    return false
  }
}

/**
 * True when a verify refusal is the single-session kick, not an
 * ordinary expiry: the refusal carries the known English message
 * (fast path), OR any refusal arrived for a token whose own exp
 * claim says it should still be valid (the localized case). @pure
 */
export function isKickedSessionError(
  errorish: unknown,
  token?: string | null
): boolean {
  const entries = errorEntries(errorish)
  if (entries.length === 0) return false
  if (
    entries.some(
      (entry) =>
        typeof entry.message === 'string' &&
        entry.message.includes(KICKED_MESSAGE)
    )
  ) {
    return true
  }
  return tokenLooksAlive(token)
}

/** Remember that THIS bounce to /login was the single-session kick. */
export function markSignedInElsewhere(): void {
  try {
    window.sessionStorage.setItem(KEY, '1')
  } catch (e) {
    // storage unavailable (private mode quotas): degrade to the
    // silent bounce, never crash the auth path.
  }
}

/** One-shot read: true exactly once after a kick was marked. */
export function consumeSignedInElsewhere(): boolean {
  try {
    const marked = window.sessionStorage.getItem(KEY) === '1'
    window.sessionStorage.removeItem(KEY)
    return marked
  } catch (e) {
    return false
  }
}
